import { useLogger } from "@kensho/lumberjack";
import { produce } from "immer";
import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from "react";
import { useParams } from "react-router";
import { ZodError } from "zod";

import { TranscriptPermissionsContext } from "../core/transcription/transcript-permissions-provider";
import { useTypedWebSocket } from "../hooks/use-typed-web-socket";
import {
  MultiplayerClientMessage,
  MultiplayerClientMessageSchema,
  MultiplayerServerMessage,
  MultiplayerServerMessageSchema,
} from "../types/schemas";
import { TranscriptSelectionNode } from "../types/types";
import { TranscriptionContext } from "./transcription-context";
import { useReconnectionNotifications } from "./use-reconnection-notification";
import { UserContext } from "./user-context";

interface MultiplayerProviderProps {
  children: React.ReactNode;
}

export interface ConnectedUser {
  clientId: string;
  sessionId: string;
  clientName: string;
  cursorPosition?: CursorDetails;
}

export interface CursorDetails {
  start: TranscriptSelectionNode | null;
  end: TranscriptSelectionNode | null;
}

interface ConnectionError {
  reason?: string;
  code?: number;
}

interface ServerError {
  type: "error";
  payload: {
    code: string;
    title: string;
    detail: string;
  };
}

interface MessageValidationError {
  type: "message-validation-error";
  error: ZodError;
}

type MultiplayerError = ServerError | MessageValidationError;

export interface MultiplayerContextType {
  authenticated: boolean;
  connectedUsers: ConnectedUser[];
  connectionError?: ConnectionError;
  error?: MultiplayerError;
  registerReceiveMessageCallback: (
    callback: (message: MultiplayerServerMessage) => void,
  ) => () => void;
  sessionId: string | null;
  sendMessage: (message: MultiplayerClientMessage) => void;
}

export const MultiplayerContext = createContext<MultiplayerContextType | null>(
  null,
);

interface MultiplayerState {
  authenticated: boolean;
  connectedUsers: ConnectedUser[];
  connectionError?: ConnectionError;
  error?: MultiplayerError;
  sessionId: string | null;
}

type MultiplayerAction =
  | {
      type: "userAuthenticated";
      authenticated: boolean;
      sessionId: string;
    }
  | { type: "userConnected"; user: ConnectedUser }
  | { type: "userDisconnected"; sessionId: string }
  | {
      type: "addReceiveMessageCallback";
      callback: (message: MultiplayerServerMessage) => void;
    }
  | {
      type: "removeReceiveMessageCallback";
      callback: (message: MultiplayerServerMessage) => void;
    }
  | { type: "connectionError"; connectionError?: ConnectionError }
  | { type: "cursorUpdate"; pos: CursorDetails; sessionId: string }
  | { type: "error"; error?: MultiplayerError }
  | { type: "reset" };

function reducer(
  state: MultiplayerState,
  action: MultiplayerAction,
): MultiplayerState {
  switch (action.type) {
    case "userAuthenticated":
      return {
        ...state,
        authenticated: action.authenticated,
        sessionId: action.sessionId,
      };
    case "userConnected":
      return {
        ...state,
        connectedUsers: [...state.connectedUsers, action.user],
      };
    case "userDisconnected":
      return {
        ...state,
        connectedUsers: state.connectedUsers.filter(
          (prevUser) => prevUser.sessionId !== action.sessionId,
        ),
      };
    case "cursorUpdate": {
      return produce(state, (draftState) => {
        const index = draftState.connectedUsers.findIndex(
          (user) => user.sessionId === action.sessionId,
        );
        if (index !== -1)
          draftState.connectedUsers[index].cursorPosition = action.pos;
      });
    }
    case "connectionError":
      return {
        authenticated: false,
        connectedUsers: [],
        connectionError: action.connectionError,
        sessionId: null,
      };
    case "error":
      return { ...state, error: action.error };
    case "reset":
      return {
        authenticated: false,
        connectedUsers: [],
        sessionId: null,
      };
    default:
      return state;
  }
}

export function MultiplayerProvider({ children }: MultiplayerProviderProps) {
  const log = useLogger();
  const { user } = use(UserContext);
  const { stage, metadata } = use(TranscriptionContext);
  const { setTranscriptPermissions } = use(TranscriptPermissionsContext);
  const { transcriptId } = useParams();
  const receiveMessageCallbacksRef = useRef<
    ((message: MultiplayerServerMessage) => void)[]
  >([]);

  const [state, dispatch] = useReducer(reducer, {
    authenticated: false,
    connectedUsers: [],
    sessionId: null,
  });

  const shouldConnect =
    stage === "POST_TRANSCRIPTION" &&
    !!transcriptId &&
    metadata?.status === "complete";

  const handleValidationError = useCallback(
    (error: ZodError, message: unknown, isClientMessage: boolean): void => {
      dispatch({
        type: "error",
        error: {
          type: "message-validation-error",
          error,
        },
      });
      log.error(error, {
        message: `${JSON.stringify(message)}`,
        isClientMessage,
      });
    },
    [log],
  );

  const handleOpen = useCallback(
    (sendMessage: (message: MultiplayerClientMessage) => void): void => {
      // send authentication message
      if (!user || !user.token) return;
      sendMessage({ type: "authenticate", payload: { token: user.token } });
      dispatch({ type: "reset" });
    },
    [user],
  );

  // server closes connection
  const handleClose = useCallback(
    (e: CloseEvent): boolean => {
      // code 1000 is a clean connection close, all else are connection errors
      if (e.code !== 1000) {
        dispatch({
          type: "connectionError",
          connectionError: { reason: e.reason, code: e.code },
        });
        log.error(`Websocket connection closed with code: ${e.code}`, {
          reason: e.reason,
        });
      }
      if (e.code === 1003 || e.code === 1008) return false;
      return true;
    },
    [log],
  );

  const handleTopLevelMessage = useCallback(
    (message: MultiplayerServerMessage): boolean => {
      switch (message.type) {
        case "authenticated":
          dispatch({
            type: "userAuthenticated",
            authenticated: true,
            sessionId: message.payload.sessionId,
          });

          break;
        case "presence-add":
          dispatch({ type: "userConnected", user: message.payload });
          break;
        case "presence-remove":
          dispatch({
            type: "userDisconnected",
            sessionId: message.payload.sessionId,
          });
          break;
        case "reconnect":
          return true;
        case "cursor-update":
          dispatch({
            type: "cursorUpdate",
            pos: { start: message.payload.start, end: message.payload.end },
            sessionId: message.payload.sessionId,
          });
          break;
        case "error":
          dispatch({ type: "error", error: message });
          log.error("Websocket message of type: error", {
            message: message.payload.toString(),
          });

          // generic errors will allow for reconnect
          if (
            message.payload.code !== "S2T-EDT-001" &&
            message.payload.code !== "S2T-EDT-002" &&
            message.payload.code !== "S2T-EDT-003"
          )
            return true;
          break;
        default:
        // noop
      }
      return false;
    },
    [log],
  );

  const receiveMessage = (message: MultiplayerServerMessage): boolean => {
    const shouldReconnect = handleTopLevelMessage(message);
    receiveMessageCallbacksRef.current.forEach((callback) => callback(message));
    return shouldReconnect;
  };

  const registerReceiveMessageCallback = useCallback(
    (callback: (message: MultiplayerServerMessage) => void): (() => void) => {
      receiveMessageCallbacksRef.current.push(callback);
      return () => {
        receiveMessageCallbacksRef.current =
          receiveMessageCallbacksRef.current.filter((cb) => cb !== callback);
      };
    },
    [],
  );

  const { sendMessage, status } = useTypedWebSocket({
    url: `${window.location.protocol === "https:" ? "wss://" : "ws://"}${window.location.host}/app/editor/v2/${transcriptId}`,
    clientMessageSchema: MultiplayerClientMessageSchema,
    serverMessageSchema: MultiplayerServerMessageSchema,
    onValidationError: handleValidationError,
    onMessage: receiveMessage,
    onClose: handleClose,
    onOpen: handleOpen,
    shouldConnect,
  });

  useReconnectionNotifications(status, shouldConnect);

  useEffect(() => {
    if (!shouldConnect) {
      dispatch({ type: "reset" });
    }
  }, [shouldConnect]);

  // switch to read-only mode if hitting an error
  useEffect(() => {
    if (state.error || state.connectionError)
      setTranscriptPermissions({ edit: false });
  }, [state.error, state.connectionError, setTranscriptPermissions]);

  const value = useMemo<MultiplayerContextType>(
    () => ({
      ...state,
      sendMessage,
      registerReceiveMessageCallback,
    }),
    [state, sendMessage, registerReceiveMessageCallback],
  );

  return <MultiplayerContext value={value}>{children}</MultiplayerContext>;
}
